<template>
  <div>
    <widget>
      <template slot="title">
        Externe Fahrzeugkonfiguration
      </template>
      <template slot="content">
        <div
          class="p-4"
          style="margin-bottom: 20px;"
        >
          <mia-instruction
            style="padding: 1rem; font-size: 1rem;"
            :done="hasMakeAndModel"
          >
            Bitte wählen Sie zunächst den Hersteller und das Modell aus, für die eine Fahrzeugkonfiguration importiert werden soll.
          </mia-instruction>

          <div style="margin: 10px 0;">
            <mia-dealer-feature-notice
              v-if="make.name === 'SEAT' && !isAllowedDealer('configuration_importer_seat')"
              feature="configuration_importer_seat"
            />
            <mia-dealer-feature-notice
              v-if="make.name === 'CUPRA' && !isAllowedDealer('configuration_importer_cupra')"
              feature="configuration_importer_cupra"
            />
            <mia-dealer-feature-notice
              v-if="make.name === 'SKODA' && !isAllowedDealer('configuration_importer_skoda')"
              feature="configuration_importer_skoda"
            />
            <mia-dealer-feature-notice
              v-if="make.name === 'VW' && !isAllowedDealer('configuration_importer_vw')"
              feature="configuration_importer_vw"
            />
            <mia-dealer-feature-notice
              v-if="make.name === 'AUDI' && !isAllowedDealer('configuration_importer_audi')"
              feature="configuration_importer_audi"
            />
          </div>
        </div>
        <div class="row">
          <section class="col col-md-4">
            <label>Hersteller</label>
            <select
              v-model="make"
              class="form-control"
              @change="onMakeChanged"
            >
              <option value="">
                -- Bitte wählen --
              </option>
              <option
                v-for="m in makes"
                :key="m.id"
                :value="m"
              >
                {{ m.description }}
              </option>
            </select>
          </section>
          <section
            v-if="hasModels"
            class="col col-md-4"
          >
            <label>Modell</label>
            <select
              ref="modelInput"
              v-model="model"
              class="form-control"
            >
              <option value="">
                -- Bitte wählen --
              </option>
              <option
                v-for="m in models"
                :key="m.id"
                :value="m"
              >
                {{ m.description }}
              </option>
            </select>
          </section>

          <section
            v-if="hasModel"
            class="col col-md-4"
          >
            <label>
              Baureihe
              <select
                v-model="config.configuration.modelRange"
                class="form-control"
              >
                <option value="">- ohne Baureihe -</option>
                <option
                  v-for="modelRange in modelRanges"
                  :key="modelRange.name"
                  :value="modelRange.name"
                >
                  {{ modelRange.name }}
                </option>
              </select>
            </label>
          </section>

          <section
            v-if="hasModel"
            class="col col-md-4"
          >
            <label>
              Ausführung
              <select
                v-model="config.configuration.line"
                class="form-control"
              >
                <option value="">- ohne Ausführung -</option>
                <option
                  v-for="trimline in trimlines"
                  :key="trimline.name"
                  :value="trimline.name"
                >
                  {{ trimline.name }}
                </option>
              </select>
            </label>
          </section>

          <section
            v-if="askForOfferPdf"
            class="col col-md-4"
          >
            <label>Angebots-PDF aus {{ make.description }} Konfigurator</label>
            <input
              type="file"
              accept="application/pdf"
              class="form-control"
              @change="offerPdf = $event.target.files[0]"
            >
          </section>
          <section
            v-if="askForCode"
            class="col col-md-4"
          >
            <label>{{ codeLabel }}</label>
            <input
              v-model="code"
              type="text"
              class="uppercase form-control"
            >
          </section>
        </div>
        <div class="row">
          <section
            v-if="hasModel && askForPrice"
            class="col col-md-4"
          >
            <label>Gesamtpreis inkl. MwSt.</label>
            <input
              v-model="config.configuration.totalPrice.gross"
              type="text"
              class="uppercase form-control"
            >
          </section>
        </div>
        <div
          v-if="status && status.length > 0"
          class="row"
        >
          <section
            v-if="failed"
            class="text-white bg-gray-600 border col col-md-12"
            style="padding: 5px;"
          >
            Fehler beim Prüfen der Konfiguration
          </section>
          <section
            v-else
            class="text-white bg-gray-600 border col col-md-12"
            style="padding: 5px;"
          >
            <div
              v-for="message in status"
              :key="message"
              v-text="message"
            />
          </section>
        </div>
        <div
          v-if="isReady"
          class="flex items-start"
          :class="{ 'justify-between': !hasConfiguratorInterpreter || configurationIsLoaded || hasFailed }"
        >
          <button
            v-if="hasConfiguratorInterpreter && !configurationIsLoaded"
            class="btn btn-success"
            style="margin-right: 10px;"
            @click="prepare"
          >
            {{ buttonLabel }}
          </button>

          <template v-if="!hasConfiguratorInterpreter || configurationIsLoaded || hasFailed">
            <div class="w-full max-w-xs">
              <template v-if="allowCustomListPrice">
                <label for="customNetListPriceAtOrdertime">
                  UPE zum Bestellzeitpunkt Netto
                </label>
                <input
                  id="customNetListPriceAtOrdertime"
                  v-model="customNetListPriceAtOrdertime"
                  type="text"
                  class="form-control"
                >
              </template>
            </div>

            <button
              class="btn btn-success"
              @click="saveConfig"
            >
              {{ saveConfigLabel }}
            </button>
          </template>
        </div>

        <a
          v-if="isRoot && !showConfigObject"
          @click="showConfigObject=true"
        >Konfigurationsobjekt anzeigen (root)</a>
        <pre v-if="isRoot && showConfigObject">{{ config }}</pre>

        <div
          v-if="isLoading"
          class="flex justify-center py-4"
        >
          <mia-wave-loader />
        </div>
        <div
          v-else-if="configurationIsLoaded && !failed"
          class="row"
          style="margin-top: 25px"
        >
          <table style="border-spacing: 10px; border-collapse: separate;">
            <tr>
              <th>Fahrzeug:</th>
              <td class="text-lg">
                <strong>{{ carTitle }}</strong>
              </td>
            </tr>
            <tr>
              <th>UVP:</th>
              <td
                class="text-lg"
                style="padding-bottom:25px;"
              >
                {{ config.configuration.totalPrice.gross | price }}
              </td>
            </tr>
            <tr>
              <th class="align-top">
                Außenfarbe:
              </th>
              <td>
                <span
                  v-if="!hasColor"
                  style="padding-left: 10px;"
                >Keine Sonderausstattung gefunden</span>
                <table
                  v-if="hasColor"
                  style="width:100%; border-spacing: 5px; border-collapse: separate;"
                >
                  <tr>
                    <td>{{ config.configuration.color.code }}</td>
                    <td>{{ config.configuration.color.label }}</td>
                    <td class="text-right">
                      <template v-if="config.configuration.color.price > 0">
                        {{ config.configuration.color.price | price }}
                      </template>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
            <tr>
              <th class="align-top">
                Interieur:
              </th>
              <td>
                <span
                  v-if="!hasInteriorColor"
                  style="padding-left: 10px;"
                >Keine Sonderausstattung gefunden</span>
                <table
                  v-if="hasInteriorColor"
                  style="width:100%; border-spacing: 5px; border-collapse: separate;"
                >
                  <tr>
                    <td>{{ config.configuration.interiorColor.code }}</td>
                    <td>{{ config.configuration.interiorColor.label }}</td>
                    <td class="text-right">
                      <template v-if="config.configuration.interiorColor.price > 0">
                        {{ config.configuration.interiorColor.price | price }}
                      </template>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
            <tr>
              <th class="align-top">
                Sonderausstattung:
              </th>
              <td>
                <span
                  v-if="!config.configuration.equipment.special"
                  style="padding-left: 10px;"
                >Keine Sonderausstattung gefunden</span>
                <table style="border-spacing: 5px; border-collapse: separate;">
                  <tr
                    v-for="item in config.configuration.equipment.special"
                    :key="item.code + item.label"
                  >
                    <td>{{ item.code }}</td>
                    <td>{{ item.label }}</td>
                    <td class="text-right">
                      {{ item.priceGross | price }}
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
            <tr>
              <th class="align-top">
                Serienausstattung:
              </th>
              <td>
                <table style="border-spacing: 5px; border-collapse: separate;">
                  <tr
                    v-for="item in config.configuration.equipment.standard"
                    :key="item.code + item.label"
                  >
                    <td>{{ item.code }}</td>
                    <td>{{ item.label }}</td>
                  </tr>
                </table>
              </td>
            </tr>
            <tr v-if="config.configuration.miscellaneousImages">
              <th class="align-top">
                Sonstige Bilder:
              </th>
              <td>
                <div class="grid grid-cols-4 gap-6">
                  <div
                    v-for="image in config.configuration.miscellaneousImages"
                    :key="image.path"
                    class="w-64 bg-gray-300 rounded border-2 hover:border-orange-mia cursor-pointer"
                    :class="{
                      'border-orange-mia': config.miscellaneousImages.find(existingImage => existingImage.id === image.path) !== undefined,
                      'border-gray-500': config.miscellaneousImages.find(existingImage => existingImage.id === image.path) === undefined,
                    }"
                    style="padding: 8px"
                    @click="toggleMiscellaneousImage(image)"
                  >
                    <div
                      class="flex items-center justify-center"
                      style="aspect-ratio: 4/3"
                    >
                      <img :src="image.base64">
                    </div>
                    <div
                      v-if="config.miscellaneousImages.find(existingImage => existingImage.id === image.path) !== undefined"
                      class="mt-8"
                    >
                      <mia-feature-select
                        label="Kategorie"
                        :value="config.miscellaneousImages.find(existingImage => existingImage.id === image.path).category"
                        @input="onMiscellaneousImageCategoryInput(image, $event)"
                      >
                        <template #options>
                          <option
                            value=""
                            style="padding-left: 8px; padding-right: 8px;"
                          >
                            Kategorie wählen
                          </option>
                          <option
                            value="tyre_label"
                            style="padding-left: 8px; padding-right: 8px;"
                          >
                            Reifenlabel
                          </option>
                        </template>
                      </mia-feature-select>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </table>
        </div>
        <div
          v-else-if="failed && status && status.length > 0"
          style="margin-top: 1.5rem"
        >
          <mia-notice
            permanent
            type="error"
          >
            <div style="padding: 0.5rem 0.75rem">
              <div class="font-semibold text-sm">
                Beim Prüfen der Konfiguration ist ein Fehler aufgetreten
              </div>

              <!-- eslint-disable vue/no-v-html -->
              <div
                style="margin-top: 0.25rem"
                class="text-sm"
                v-html="status.join('<br>')"
              />
              <!-- eslint-enable vue/no-v-html -->

              <button
                class="btn"
                style="margin-top: 1rem; background-color: #0284c7; color: #fff"
                type="button"
                @click="prepare"
              >
                Erneut versuchen
              </button>
            </div>
          </mia-notice>
        </div>
      </template>
    </widget>
  </div>
</template>

<script>
import _ from 'lodash'
import uuid from 'uuid-v4'
import { mapActions } from 'vuex'
import { cashSave, tryFocus } from '@/helpers/utils'
import { ImageImporter } from '@/helpers/configuration'
import CalculatesVat from '@/mixins/CalculatesVat'
import Instruction from '@/components/ui/Instruction.vue'
import WaveLoader from '@/components/ui/loader/WaveLoader.vue'
import Notice from '@/components/cars/edit/Notice.vue'
import FeatureSelect from '@/components/cars/edit/FeatureSelect.vue'
import Widget from '../general/widget.vue'

export default {
  components: {
    Widget,
    'mia-feature-select': FeatureSelect,
    'mia-instruction': Instruction,
    'mia-notice': Notice,
    'mia-wave-loader': WaveLoader
  },

  mixins: [CalculatesVat],

  props: {
    action: {
      type: String,
      default: 'configurator'
    },
    uuid: {
      type: String,
      default: uuid()
    },
    allowCustomListPrice: {
      type: Boolean,
      default: false
    },
    saveConfigLabel: {
      type: String,
      default: 'Konfiguration übernehmen'
    }
  },

  data() {
    return {
      externalConfig: {},
      status: [],
      failed: false,
      makes: [],
      make: '',
      models: [],
      model: '',
      modelRanges: [],
      trimlines: [],
      code: '',
      offerPdf: null,
      buttonLabel: 'Konfiguration prüfen',
      showConfigObject: false,
      config: {
        id: null,
        configuration: {
          type: 'external',
          basePrice: {
            net: null,
            gross: null
          },
          totalPrice: {
            net: null,
            gross: null
          },
          externalId: null,
          configuratorUrl: null,
          maker: null,
          model: null,
          modelRange: null,
          line: null,
          title: 'Externe Kundenkonfiguration',
          color: {
            label: '',
            code: ''
          },
          equipment: {
            standard: [],
            special: []
          },
          technicalData: {
            seats: '',
            bodyType: '',
            driveLabel: '',
            fuelTypeLabel: '',
            transmissionLabel: '',
            doors: '',
            bodyTypeLabel: '',
            drivenWheelLabel: '',
            vehicleType: '',
            wheelBase: '',
            engine: {
              acceleration: '',
              drive: '',
              cc: '',
              bore: '',
              fuelType: '',
              emissionData: {
                co2: '',
                standard: '',
                efficiency: ''
              },
              fuelConsumption: {
                combined: '',
                urban: '',
                highway: ''
              },
              power: {
                hp: '',
                kw: '',
                rpm: '',
                torque: ''
              },
              rpm: {
                maxPower: ''
              },
              speed: '',
              cylinders: ''
            },
            trailerData: {
              weightBraked: '',
              weightUnbraked: ''
            },
            track: {
              front: '',
              rear: ''
            },
            dimensions: {
              height: '',
              length: '',
              width: ''
            },
            hsn: '',
            tsn: '',
            weight: {
              kerb: '',
              gross: ''
            },
            tankCapacity: '',
            trunkVolume: '',
            tyre: {
              diameter: '',
              profile: '',
              rating: '',
              width: ''
            },
            year: ''
          },
          wltpValues: {
            electricalCo2Combined: '',
            electricalConsumptionCombined: '',
            electricalConsumptionExtraHigh: '',
            electricalConsumptionHigh: '',
            electricalConsumptionLow: '',
            electricalConsumptionMedium: '',
            electricalRangeCity: '',
            electricalRangeCombined: '',
            hybridElectricConsumptionCombinedWeighted: '',
            hybridPetrolCo2CombinedWeighted: '',
            hybridPetrolConsumptionCombinedWeighted: '',
            petrolCo2Combined: '',
            petrolConsumptionCombined: '',
            petrolConsumptionExtraHigh: '',
            petrolConsumptionHigh: '',
            petrolConsumptionLow: '',
            petrolConsumptionMedium: ''
          }
        },
        miscellaneousImages: []
      },
      customNetListPriceAtOrdertime: '',
      isLoading: false
    }
  },

  computed: {
    hasModels() {
      return (_.size(this.models) > 0)
    },

    hasModel() {
      return (_.size(this.model) > 0)
    },

    hasCode() {
      return (_.size(this.code) > 0)
    },

    hasOfferPdf() {
      return this.offerPdf !== null
    },

    hasConfiguratorInterpreter() {
      return this.hasConfiguratorInterpreterByPdf || this.hasConfiguratorInterpreterByCode
    },

    hasConfiguratorInterpreterByPdf() {
      return (
        (this.make.name === 'SEAT' && this.isAllowedDealer('configuration_importer_seat'))
        || (this.make.name === 'CUPRA' && this.isAllowedDealer('configuration_importer_cupra'))
        || (this.make.name === 'AUDI' && this.isAllowedDealer('configuration_importer_audi'))
      )
    },

    hasConfiguratorInterpreterByCode() {
      return (
        (this.make.name === 'SKODA' && this.isAllowedDealer('configuration_importer_skoda'))
        || (this.make.name === 'VW' && this.isAllowedDealer('configuration_importer_vw'))
      )
    },

    hasFailed() {
      return this.failed
    },

    isReady() {
      return (
        this.hasCode
      )
    },

    askForCode() {
      return this.hasModel && (!this.hasConfiguratorInterpreterByPdf || this.failed)
    },

    askForPrice() {
      return !this.hasConfiguratorInterpreter || this.failed
    },

    askForOfferPdf() {
      return this.hasModel && this.hasConfiguratorInterpreterByPdf
    },

    configurationIsLoaded() {
      return this.config.configuration.externalId && this.config.configuration.externalId === this.code
    },

    codeLabel() {
      if (this.make.name === 'SKODA') return 'Skoda ID'

      return `${this.make.description} Konfigurationscode`
    },

    hasMake() {
      return (_.size(this.make) > 0)
    },

    hasMakeAndModel() {
      return this.hasMake && this.hasModel
    },

    carTitle() {
      const make = this.config.configuration.maker || ''
      const model = this.config.configuration.model || ''
      const line = this.config.configuration.line || ''
      let title = this.config.configuration.title || ''
      const parts = [make, model, line]

      title = title.replace(new RegExp(make, 'i'), '')
      title = title.replace(new RegExp(model, 'i'), '')
      title = title.replace(new RegExp(line, 'i'), '')
      parts.push(title)

      let fullTitle = parts.join(' ').replace(/\s{1,}/g, ' ')

      // Temporary workaround for CUPRA Formentor, so that customer is satisfied
      fullTitle = fullTitle.replace(/^CUPRA ANDERE Formentor/i, 'CUPRA Formentor')

      return fullTitle
    },

    hasColor() {
      return !_.isEmpty(_.get(this.config, 'configuration.color.label'))
    },

    hasInteriorColor() {
      return !_.isEmpty(_.get(this.config, 'configuration.interiorColor.label'))
    }
  },

  watch: {
    offerPdf: {
      deep: true,
      handler() {
        if (this.hasOfferPdf) {
          this.parseOfferPdf()
        }
      }
    },

    make: {
      immediate: true,
      handler() {
        this.models = []
        this.model = []
        this.modelRanges = []
        this.trimlines = []
      }
    },

    model: {
      immediate: true,
      handler(model) {
        this.loadModelRangesAndTrimlines(
          this.make.name,
          model.name,
          null
        )
      }
    }
  },

  created() {
    this.api.mobile.manufacturers().then((data) => {
      this.makes = data.values
    })
  },

  methods: {
    ...mapActions([
      'storeCashSaleConfiguration'
    ]),

    onMakeChanged() {
      this.api.mobile.models(this.make.name).then((data) => {
        this.models = data.values

        if (this.hasModels) {
          this.$nextTick(() => {
            tryFocus(this.$refs.modelInput)
          })
        }
      })
    },

    prepare() {
      this.failed = false
      this.setBasics()

      if (this.hasConfiguratorInterpreter) {
        this.buttonLabel = 'Bitte warten...'
        this.status = ['Ausstattung wird abgeglichen...']
        this.isLoading = true

        this.api.configurator.getExternal(this.make.name.toLowerCase(), this.code, this.uuid, this.action === 'createCar')
          .then((data) => {
            if (typeof data.error === 'undefined') {
              this.status = ['Die Ausstattung wurde erfolgreich abgeglichen']
              this.externalConfig = data
              this.match()

              if (this.allowCustomListPrice) {
                this.customNetListPriceAtOrdertime = `${this.config.configuration.totalPrice.net}`.replace('.', ',')
              }
            } else {
              this.status = data.error
              this.failed = true
            }
          })
          .catch((e) => {
            this.failed = true

            if (e.response.data && e.response.data.error && Array.isArray(e.response.data.error) && e.response.data.error.length > 0) {
              this.status = e.response.data.error
            } else {
              this.status = ['Es ist ein unbekannter Fehler aufgetreten']
            }
          })
          .finally(() => {
            this.isLoading = false
          })
      } else {
        this.saveConfig()
      }
    },

    parseOfferPdf() {
      this.status = ['Lese Angebots PDF ein']
      this.isLoading = true
      const formData = new FormData()
      formData.set('configurationId', this.uuid)
      formData.set('offer', this.offerPdf)

      axios.post(`${window.baseUrl}/externalconfigurator/parseOfferPdf/${this.make.name}`, formData, {
        headers: {
          'content-type': 'multipart/form-data'
        }
      })
        .then(({ data }) => {
          if (data.code) {
            this.code = data.code
            this.status = [`Angebots PDF erfolgreich geladen (${data.code.split('|')[0]})`]
          } else {
            this.status = data.error
            this.failed = true
          }
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    match() {
      if (this.make.name.toLowerCase() === 'seat') this.matchSeatV2()
      if (this.make.name.toLowerCase() === 'cupra') this.matchCupra()
      if (this.make.name.toLowerCase() === 'skoda') this.matchSkoda()
      if (this.make.name.toLowerCase() === 'vw') this.matchVolkswagen()
      if (this.make.name.toLowerCase() === 'audi') this.matchAudi()
    },

    matchSeatV2() {
      this.config.configuration = Object.assign(this.config.configuration, this.externalConfig.configuration)
      this.config.configuration.technicalData.hsn = 7593
      this.config.images = ImageImporter.factory('Seat').import(this.externalConfig)
      this.setBasics()
    },

    matchSeat() {
      this.config.configuration.technicalData.hsn = 7593
      this.config.configuration.configuratorUrl = `https://konfigurator.seat.de/seat-cc/configurator/LIVE/007/DEFAULT/de/load/${this.code}`

      this.config.configuration.basePrice = {
        net: (this.subVat(cashSave(this.externalConfig.configuration.totalPrices[Object.keys(this.externalConfig.configuration.totalPrices)[0]].basePrice))),
        gross: cashSave(this.externalConfig.configuration.totalPrices[Object.keys(this.externalConfig.configuration.totalPrices)[0]].basePrice)
      }
      this.config.configuration.totalPrice = {
        net: (this.subVat(cashSave(this.externalConfig.configuration.totalPrices[Object.keys(this.externalConfig.configuration.totalPrices)[0]].totalPrice))),
        gross: cashSave(this.externalConfig.configuration.totalPrices[Object.keys(this.externalConfig.configuration.totalPrices)[0]].totalPrice)
      }

      if (this.config.configuration.line === '') {
        this.config.configuration.line = this.externalConfig.model.carlineTexts.name
      }

      this.config.configuration.title = `${this.externalConfig.modelTitle} ${this.externalConfig.model.texts.text}`
      this.config.configuration.title = this.config.configuration.title.replace(this.config.configuration.maker, '')
      this.config.configuration.title = this.config.configuration.title.replace(this.config.configuration.model, '')
      this.config.configuration.title = this.config.configuration.title.replace(this.config.configuration.line, '')
      this.config.configuration.modelCode = this.externalConfig.configuration.modelCode || ''

      this.config.images = ImageImporter.factory('Seat').import(this.externalConfig)

      Object.keys(this.externalConfig.configuration.partStatus).forEach((el) => {
        const partStatus = this.externalConfig.configuration.partStatus[el]
        const partPrice = (typeof partStatus.prices !== 'undefined' ? cashSave(partStatus.prices[Object.keys(partStatus.prices)[0]]) : 0)

        if (partStatus.selected) {
          if (this.externalConfig.model.parts[el].standard) {
            let code = el.substr(el.length - 3).trim()
            const matches = el.match(/[A-Z0-9]{3} ([A-Z0-9]{4})/)

            if (matches !== null) {
              // eslint-disable-next-line prefer-destructuring
              code = matches[1]
            }

            this.config.configuration.equipment.standard.push({
              label: this.externalConfig.model.parts[el].texts.text,
              code,
              priceNet: 0,
              priceGross: 'Serie'
            })
          } else {
            let code = el.substr(el.length - 3).trim()
            const matches = el.match(/[A-Z0-9]{3} ([A-Z0-9]{4})/)

            if (matches !== null) {
              // eslint-disable-next-line prefer-destructuring
              code = matches[1]
            }

            this.config.configuration.equipment.special.push({
              label: this.externalConfig.model.parts[el].texts.text,
              code,
              priceNet: this.subVat(partPrice),
              priceGross: partPrice
            })
          }
        }
      })

      try {
        const techData = this.externalConfig.configuration.techData[Object.keys(this.externalConfig.configuration.techData)[0]].groupings

        let consumptionUrban = Object.values(techData).find(el => el.key === 'consumptionUrban').values
        consumptionUrban = consumptionUrban[Object.keys(consumptionUrban)[0]].sortValue

        let consumptionExtraUrban = Object.values(techData).find(el => el.key === 'consumptionExtraUrban').values
        consumptionExtraUrban = consumptionExtraUrban[Object.keys(consumptionExtraUrban)[0]].sortValue

        let consumptionCombined = Object.values(techData).find(el => el.key === 'consumptionCombined').values
        consumptionCombined = consumptionCombined[Object.keys(consumptionCombined)[0]].sortValue

        let emissionCombined = Object.values(techData).find(el => el.key === 'emissionCombined').values
        emissionCombined = emissionCombined[Object.keys(emissionCombined)[0]].sortValue

        let efficiencyClass = Object.values(techData).find(el => el.key === 'efficiencyClass').values
        efficiencyClass = efficiencyClass[Object.keys(efficiencyClass)[0]].viewValue

        this.config.configuration.technicalData.engine.fuelConsumption.combined = consumptionCombined
        this.config.configuration.technicalData.engine.fuelConsumption.urban = consumptionUrban
        this.config.configuration.technicalData.engine.fuelConsumption.highway = consumptionExtraUrban
        this.config.configuration.technicalData.engine.emissionData.co2 = emissionCombined
        this.config.configuration.technicalData.engine.emissionData.efficiency = efficiencyClass
        this.config.configuration.technicalData.year = this.externalConfig.model.modelYear
      } catch (e) {
        console.log('error matching tech data', e)
      }

      if (typeof this.externalConfig.modelDetails !== 'undefined') {
        const [fuelType] = this.externalConfig.modelDetails.filterValues.fueltype

        this.config.configuration.technicalData.fuelTypeLabel = fuelType
        this.config.configuration.technicalData.engine.fuelType = fuelType

        const modelTechData = this.externalConfig.modelDetails.techData.find(el => el.key === 'additionalDescriptions').groupings

        let maxTorque = Object.values(modelTechData).find(el => el.key === 'maxTorque').values
        maxTorque = maxTorque[Object.keys(maxTorque)[0]].sortValue

        let maxSpeed = Object.values(modelTechData).find(el => el.key === 'maxSpeed').values
        maxSpeed = maxSpeed[Object.keys(maxSpeed)[0]].sortValue

        let maxPower = Object.values(modelTechData).find(el => el.key === 'maxPower').values
        maxPower = maxPower[Object.keys(maxPower)[0]].sortValue

        this.config.configuration.technicalData.engine.power.torque = maxTorque
        this.config.configuration.technicalData.engine.power.hp = Math.round((maxPower * 1.35962))
        this.config.configuration.technicalData.engine.power.kw = maxPower
        this.config.configuration.technicalData.engine.speed = maxSpeed
      }
    },

    matchSeatV2() {
      this.config.configuration = Object.assign(this.config.configuration, this.externalConfig.configuration)
      this.config.configuration.technicalData.hsn = 7593
      this.config.images = ImageImporter.factory('Seat').import(this.externalConfig)
      this.setBasics()
    },

    matchCupra() {
      this.config.configuration = Object.assign(this.config.configuration, this.externalConfig.configuration)
      this.config.images = ImageImporter.factory('Cupra').import(this.externalConfig)
    },

    matchAudi() {
      this.config.configuration = Object.assign(this.config.configuration, this.externalConfig.configuration)
      // this.config.images = ImageImporter.factory('Cupra').import(this.externalConfig)
    },

    matchSkoda() {
      this.config.configuration.technicalData.hsn = 8004
      this.config.configuration.configuratorUrl = 'https://cc.skoda-auto.com/deu/de-DE/'
      this.config.images = ImageImporter.factory('Skoda').import(this.externalConfig)
      this.config.configuration.modelCode = this.externalConfig.MbvKey || ''

      if (this.externalConfig.InteriorCode && this.externalConfig.InteriorName && this.externalConfig.InteriorPrice) {
        this.config.configuration.interiorColor = {
          code: this.externalConfig.InteriorCode,
          label: this.externalConfig.InteriorName,
          price: this.externalConfig.InteriorPrice.price || 0
        }
      }

      this.config.configuration.color = {
        label: this.externalConfig.ExteriorName,
        code: this.externalConfig.ExteriorId,
        priceNet: this.subVat(this.externalConfig.ExteriorPrice.price),
        priceGross: this.externalConfig.ExteriorPrice.price
      }

      this.config.configuration.basePrice = {
        net: this.subVat(this.externalConfig.Price.price),
        gross: this.externalConfig.Price.price
      }

      this.config.configuration.totalPrice = {
        net: this.subVat(this.externalConfig.TotalPrice.price),
        gross: this.externalConfig.TotalPrice.price
      }

      if (this.config.configuration.line === '') {
        this.config.configuration.line = this.externalConfig.ModelName || this.externalConfig.EquipmentName
      }

      this.config.configuration.title = `${this.externalConfig.EquipmentName} ${this.externalConfig.MotorName}`.trim()
      this.config.configuration.technicalData.year = this.externalConfig.ModelYear

      Object.values(this.externalConfig.ExtraEquipments).forEach((category) => {
        Object.values(category.Items).forEach((el) => {
          this.config.configuration.equipment.special.push({
            label: el.Name,
            code: el.Code,
            priceNet: this.subVat(el.Price.price),
            priceGross: el.Price.price
          })
        })
      })

      Object.values(this.externalConfig.GroupedStandardEquipments).forEach((category) => {
        Object.values(category.Items).forEach((el) => {
          if (el.Name.substring(0, 4).toLowerCase() !== 'ohne') {
            this.config.configuration.equipment.standard.push({
              label: el.Name,
              code: el.Code,
              priceNet: 0,
              priceGross: 'Serie'
            })
          }
        })
      })

      Object.values(this.externalConfig.GroupedTechnicalData).forEach((el) => {
        console.log(el)

        if (el.Id === 'Performance') {
          this.config.configuration.technicalData.engine.emissionData.efficiency = _.get(Object.values(el.Items).find(child => child.Id === 'Energy efficiency ratings'), 'Value')
          const torque = String(_.get(Object.values(el.Items).find(child => child.Id === 'MaxTorque'), 'Value'))
          this.config.configuration.technicalData.engine.power.torque = cashSave(torque.substring(0, torque.indexOf(' Nm')))
          this.config.configuration.technicalData.engine.power.hp = parseInt(Math.round(cashSave(_.get(Object.values(el.Items).find(child => child.Id === 'MaxPerformance'), 'Value')) * 1.35962), 10)
          this.config.configuration.technicalData.engine.power.kw = cashSave(_.get(Object.values(el.Items).find(child => child.Id === 'MaxPerformance'), 'Value'))
          this.config.configuration.technicalData.engine.speed = cashSave(_.get(Object.values(el.Items).find(child => child.Id === 'MaxSpeed'), 'Value'))
          this.config.configuration.technicalData.engine.acceleration = cashSave(_.get(Object.values(el.Items).find(child => child.Id === 'Acceleration'), 'Value'))
        }

        if (el.Id === 'EngineTransmissioElectric') {
          this.config.configuration.technicalData.engine.cc = cashSave(_.get(Object.values(el.Items).find(child => child.Id === 'CubicCapacity'), 'Value'))
          this.config.configuration.technicalData.engine.cylinders = cashSave(_.get(Object.values(el.Items).find(child => child.Id === 'CylinderNumber'), 'Value'))
          this.config.configuration.technicalData.engine.emissionData.co2 = cashSave(_.get(Object.values(el.Items).find(child => child.Id === 'Co2Emissions.Combined'), 'Value'))
          this.config.configuration.technicalData.engine.fuelType = _.get(Object.values(el.Items).find(child => child.Id === 'FuelType'), 'Value')
          this.config.configuration.technicalData.engine.fuelConsumption.combined = cashSave(_.get(Object.values(el.Items).find(child => ['Fuel consumption - combined', 'FuelConsumption.Combined'].includes(child.Id)), 'Value'))
          this.config.configuration.technicalData.engine.fuelConsumption.urban = cashSave(_.get(Object.values(el.Items).find(child => child.Id === 'FuelConsumption.Urban'), 'Value'))
          this.config.configuration.technicalData.engine.fuelConsumption.highway = cashSave(_.get(Object.values(el.Items).find(child => child.Id === 'FuelConsumption.ExtraUrban'), 'Value'))
          this.config.configuration.technicalData.tankCapacity = cashSave(_.get(Object.values(el.Items).find(child => child.Id === 'FuelTankContent'), 'Value'))
        }

        if (el.Id === 'Weights') {
          this.config.configuration.technicalData.weight.gross = cashSave(_.get(Object.values(el.Items).find(child => child.Id === 'KerbWeightMin'), 'Value'))
        }

        if (el.Id === 'ExteriorDimensions') {
          this.config.configuration.technicalData.dimensions.length = cashSave(_.get(Object.values(el.Items).find(child => child.Id === 'Length'), 'Value'))
          this.config.configuration.technicalData.dimensions.width = cashSave(_.get(Object.values(el.Items).find(child => child.Id === 'Width'), 'Value'))
          this.config.configuration.technicalData.dimensions.height = cashSave(_.get(Object.values(el.Items).find(child => child.Id === 'Height'), 'Value'))
          this.config.configuration.technicalData.trunkVolume = cashSave((_.get(Object.values(el.Items).find(child => child.Id === 'LuggageCapacity'), 'Value') || '').split('/')[0].trim())
        }

        if (el.Id === 'WLTP') {
          if (!this.config.configuration.technicalData.engine.emissionData.efficiency) {
            this.config.configuration.technicalData.engine.emissionData.efficiency = _.get(Object.values(el.Items).find(child => child.Id === 'energyEfficiencyClass'), 'Value')
          }
        }
      })

      if (this.externalConfig.Responses?.modelFilter?.technicalParameters?.items) {
        this.externalConfig.Responses.modelFilter.technicalParameters.items.forEach((item) => {
          // eslint-disable-next-line default-case
          switch (item.id) {
            case 'ecs.wltpConsumptionCombined':
              if (this.externalConfig.FuelTypeCode === 'electricity') {
                this.config.configuration.wltpValues.electricalConsumptionCombined = parseFloat(`${item.minValue}`.replace(',', '.'))
              } else {
                this.config.configuration.wltpValues.petrolConsumptionCombined = parseFloat(`${item.minValue}`.replace(',', '.'))
              }
              break

            case 'ecs.wltpRangeCombined':
              this.config.configuration.wltpValues.electricalRangeCombined = parseFloat(`${item.minValue}`.replace(',', '.'))
              break

            case 'ecs.wltpEmissionCombined':
              if (this.externalConfig.FuelTypeCode === 'electricity') {
                this.config.configuration.wltpValues.electricalCo2Combined = parseFloat(`${item.minValue}`.replace(',', '.'))
              } else {
                this.config.configuration.wltpValues.petrolCo2Combined = parseFloat(`${item.minValue}`.replace(',', '.'))
              }
              break

            case 'ecs.wltpRangeCity':
              this.config.configuration.wltpValues.electricalRangeCity = parseFloat(`${item.minValue}`.replace(',', '.'))
              break

            case 'ecs.wltpConsumptionLow':
              if (this.externalConfig.FuelTypeCode === 'electricity') {
                this.config.configuration.wltpValues.electricalConsumptionLow = parseFloat(`${item.minValue}`.replace(',', '.'))
              } else {
                this.config.configuration.wltpValues.petrolConsumptionLow = parseFloat(`${item.minValue}`.replace(',', '.'))
              }
              break

            case 'ecs.wltpConsumptionMedium':
              if (this.externalConfig.FuelTypeCode === 'electricity') {
                this.config.configuration.wltpValues.electricalConsumptionMedium = parseFloat(`${item.minValue}`.replace(',', '.'))
              } else {
                this.config.configuration.wltpValues.petrolConsumptionMedium = parseFloat(`${item.minValue}`.replace(',', '.'))
              }
              break

            case 'ecs.wltpConsumptionHigh':
              if (this.externalConfig.FuelTypeCode === 'electricity') {
                this.config.configuration.wltpValues.electricalConsumptionHigh = parseFloat(`${item.minValue}`.replace(',', '.'))
              } else {
                this.config.configuration.wltpValues.petrolConsumptionHigh = parseFloat(`${item.minValue}`.replace(',', '.'))
              }
              break

            case 'ecs.wltpConsumptionExtraHigh':
              if (this.externalConfig.FuelTypeCode === 'electricity') {
                this.config.configuration.wltpValues.electricalConsumptionExtraHigh = parseFloat(`${item.minValue}`.replace(',', '.'))
              } else {
                this.config.configuration.wltpValues.petrolConsumptionExtraHigh = parseFloat(`${item.minValue}`.replace(',', '.'))
              }
              break
          }
        })
      }
    },

    matchVolkswagen() {
      this.config.configuration.technicalData.hsn = '0603'
      this.config.configuration.configuratorUrl = 'https://www.volkswagen.de/de/modelle-und-konfigurator.html'
      this.config.configuration.color = {
        label: this.externalConfig.consolidatedData.exterior.name,
        code: this.externalConfig.consolidatedData.exterior.code,
        priceNet: this.subVat(this.externalConfig.consolidatedData.exterior.prices[0].price),
        priceGross: this.externalConfig.consolidatedData.exterior.prices[0].price
      }

      this.config.configuration.basePrice = {
        net: this.subVat(this.externalConfig.consolidatedData.configurationPrice.model[0].price),
        gross: this.externalConfig.consolidatedData.configurationPrice.model[0].price
      }
      this.config.configuration.totalPrice = {
        net: this.subVat(this.externalConfig.consolidatedData.configurationPrice.total[0].price),
        gross: this.externalConfig.consolidatedData.configurationPrice.total[0].price
      }

      if (this.config.configuration.line === '') {
        this.config.configuration.line = this.externalConfig.consolidatedData.carlineName
      }

      this.config.configuration.title = this.externalConfig.consolidatedData.modelLongName
      this.config.configuration.technicalData.year = this.externalConfig.consolidatedData.modelYear

      this.config.images = ImageImporter.factory('Volkswagen').import(this.externalConfig)

      Object.values(this.externalConfig.expandedConfiguration.features).forEach((feature) => {
        if (!feature.isStandard) {
          const price = (typeof feature.prices !== 'undefined' ? feature.prices[0].price : 0)
          this.config.configuration.equipment.special.push({
            label: feature.name,
            code: feature.code,
            priceNet: this.subVat(price),
            priceGross: price
          })
        } else if (feature.name.substring(0, 4).toLowerCase() !== 'ohne') {
          this.config.configuration.equipment.standard.push({
            label: feature.name,
            code: feature.code,
            priceNet: 0,
            priceGross: 0
          })
        }
      })

      const { consumptions } = this.externalConfig.consolidatedData.efficiencyLabel.consumptionEmissions[0]
      const { emissions } = this.externalConfig.consolidatedData.efficiencyLabel.consumptionEmissions[0]
      this.config.configuration.technicalData.fuelTypeLabel = this.externalConfig.consolidatedData.modelFilters.find(el => el.type === 'FuelType').text
      this.config.configuration.technicalData.engine.fuelType = this.externalConfig.consolidatedData.modelFilters.find(el => el.type === 'FuelType').text
      this.config.configuration.technicalData.engine.power.kw = this.externalConfig.consolidatedData.technicalData.power.numberValue
      this.config.configuration.technicalData.engine.power.hp = Math.round((this.config.configuration.technicalData.engine.power.kw * 1.35962))
      this.config.configuration.technicalData.engine.cc = _.get(this.externalConfig.consolidatedData.technicalData, 'capacity.numberValue', 0)
      this.config.configuration.technicalData.weight.gross = this.externalConfig.consolidatedData.technicalData.curbWeight.numberValue
      this.config.configuration.technicalData.engine.fuelConsumption.combined = parseFloat(typeof consumptions.find(el => el.kind === 'Combined') !== 'undefined' ? consumptions.find(el => el.kind === 'Combined').value.value : 0)
      this.config.configuration.technicalData.engine.fuelConsumption.urban = parseFloat(typeof consumptions.find(el => el.kind === 'Urban') !== 'undefined' ? consumptions.find(el => el.kind === 'Urban').value.value : 0)
      this.config.configuration.technicalData.engine.fuelConsumption.highway = parseFloat(typeof consumptions.find(el => el.kind === 'ExtraUrban') !== 'undefined' ? consumptions.find(el => el.kind === 'ExtraUrban').value.value : 0)
      this.config.configuration.technicalData.engine.emissionData.co2 = parseFloat(emissions.find(el => el.kind === 'Combined').value.value)
      this.config.configuration.technicalData.engine.emissionData.efficiency = this.externalConfig.consolidatedData.efficiencyLabel.energyEfficiencyClass.value

      axios.post(`${window.baseUrl}/externalconfigurator/info_vw_model_overview/${this.externalConfig.consolidatedData.carlineKey}`)
        .then(({ data }) => {
          if (data.url) {
            this.config.configuration.configuratorUrl = `https://www.volkswagen.de/de/modelle-und-konfigurator${data.url}.html#MOFA`
          }
        })
    },

    setBasics() {
      this.config.id = this.uuid
      this.config.configuration.uuid = this.uuid
      this.config.configuration.externalId = this.code
      this.config.configuration.maker = this.make.name
      this.config.configuration.model = this.model.name
    },

    saveConfig() {
      this.status = ['Übernehme Konfiguration...']
      this.setBasics()

      if (this.action === 'configurator') {
        if (this.allowCustomListPrice && `${this.customNetListPriceAtOrdertime}`.trim() !== '') {
          const customNetListPriceAtOrdertime = cashSave(this.customNetListPriceAtOrdertime)
          const configurationNetListPrice = cashSave(this.config.configuration.totalPrice.net)

          if (customNetListPriceAtOrdertime !== configurationNetListPrice) {
            this.config.configuration.totalPrice.net = customNetListPriceAtOrdertime
            this.config.configuration.totalPrice.gross = parseFloat(this.addVat(customNetListPriceAtOrdertime))
          }
        }

        this.api.configurator.save(this.config).then(() => {
          this.api.configurator.get(this.uuid).then((response) => {
            if (response) {
              this.storeCashSaleConfiguration(response)
            }

            this.$router.push({
              name: 'offers.accessories'
            })
          })
        })
      } else if (this.action === 'createCar') {
        this.api.configurator.getPrepared(this.config).then((response) => {
          this.$emit('updatecar', response)
        })
      } else {
        throw new Error('Unknown action type for external-configurator')
      }
    },

    loadModelRangesAndTrimlines(make, model, initialRegistration, modelRange) {
      // eslint-disable-next-line no-param-reassign
      modelRange = (typeof modelRange === 'undefined' ? '' : modelRange)
      if (_.isEmpty(make)) return
      if (_.isEmpty(model)) return
      const date = new Date()

      axios.get(`${window.baseUrl}/car/modelrange/${make}/${model}/${date.getFullYear()}${(`0${date.getMonth() + 1}`).slice(-2)}`)
        .then(({ data }) => {
          this.modelRanges = data.values
          if (data.values && Object.values(data.values).length === 1) this.config.configuration.modelRange = Object.values(data.values)[0].name
        })

      axios.get(`${window.baseUrl}/car/trimlines/${make}/${model}/${date.getFullYear()}${(`0${date.getMonth() + 1}`).slice(-2)}/${modelRange}`)
        .then(({ data }) => {
          this.trimlines = data.values
          if (data.values && Object.values(data.values).length === 1) this.config.configuration.line = Object.values(data.values)[0].name
        })
    },

    toggleMiscellaneousImage(image) {
      if (this.config.miscellaneousImages.find(existingImage => existingImage.id === image.path) !== undefined) {
        this.config.miscellaneousImages = [...this.config.miscellaneousImages].filter(existingImage => existingImage.id !== image.path)
        return
      }

      this.config.miscellaneousImages = [...this.config.miscellaneousImages, {
        id: image.path,
        base64: image.base64,
        category: 'tyre_label'
      }]
    },

    onMiscellaneousImageCategoryInput(image, category) {
      this.config.miscellaneousImages = [...this.config.miscellaneousImages.map((existingImage) => {
        if (existingImage.id === image.path) {
          return {
            ...existingImage,
            category
          }
        }

        return existingImage
      })]
    }
  }
}

</script>
